import { useState, useEffect } from 'react';
import { loadDefaultFilterPreset, hasDefaultFilterPreset } from '../components/ui';

/**
 * Custom hook for managing filter state
 * Extracted from GenericFormsPage for better organization
 */
export const useFilterState = (entityName, config = null) => {
  // Filter states
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [unifiedFilters, setUnifiedFilters] = useState({});
  const [hasLoadedInitialFilters, setHasLoadedInitialFilters] = useState(false);
  const [activeFilterTab, setActiveFilterTab] = useState(() => {
    // Initialize with the first filter group if available
    if (config?.filterGroups && config.filterGroups.length > 0) {
      return config.filterGroups[0].name;
    }
    return null;
  });
  
  // Filter presets
  const [savedFilterPresets, setSavedFilterPresets] = useState([]);
  const [showFilterPresets, setShowFilterPresets] = useState(false);

  // Load saved filter presets
  useEffect(() => {
    const tableId = entityName.toLowerCase();
    const savedPresets = localStorage.getItem(`filter-presets-${tableId}`);
    if (savedPresets) {
      try {
        const parsedPresets = JSON.parse(savedPresets);
        setSavedFilterPresets(parsedPresets);
      } catch (error) {
        console.error('Error loading filter presets:', error);
        setSavedFilterPresets([]);
      }
    } else {
      setSavedFilterPresets([]);
    }
  }, [entityName]);

  // Update activeFilterTab when config changes
  useEffect(() => {
    if (config?.filterGroups && config.filterGroups.length > 0) {
      // If current tab doesn't exist in new config, reset to first group
      const currentTabExists = config.filterGroups.some(group => group.name === activeFilterTab);
      if (!currentTabExists) {
        setActiveFilterTab(config.filterGroups[0].name);
      }
    } else {
      // No filter groups, set to null
      setActiveFilterTab(null);
    }
  }, [config?.filterGroups, activeFilterTab]);

  // Load default filter preset on component mount (only once)
  useEffect(() => {
    if (!hasLoadedInitialFilters) {
      const tableId = entityName.toLowerCase();
      
      if (hasDefaultFilterPreset(tableId)) {
        const defaultFilters = loadDefaultFilterPreset(tableId);
        if (defaultFilters) {
          setUnifiedFilters(defaultFilters);
        }
      }
      setHasLoadedInitialFilters(true);
    }
  }, [entityName, hasLoadedInitialFilters]);

  // Apply filter preset from dropdown
  const handleApplyFilterPreset = (presetId) => {
    if (presetId === '') {
      // Clear filters if "All" is selected
      setUnifiedFilters({});
      return;
    }
    
    const preset = savedFilterPresets.find(p => p.id === presetId);
    if (preset) {
      setUnifiedFilters(preset.filters);
    }
  };

  // Refresh filter presets (called when presets are updated)
  const refreshFilterPresets = () => {
    const tableId = entityName.toLowerCase();
    const savedPresets = localStorage.getItem(`filter-presets-${tableId}`);
    if (savedPresets) {
      try {
        setSavedFilterPresets(JSON.parse(savedPresets));
      } catch (error) {
        console.error('Error loading filter presets:', error);
        setSavedFilterPresets([]);
      }
    } else {
      setSavedFilterPresets([]);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setUnifiedFilters({});
  };

  return {
    // Filter state
    filtersVisible,
    setFiltersVisible,
    unifiedFilters,
    setUnifiedFilters,
    activeFilterTab,
    setActiveFilterTab,
    
    // Filter presets
    savedFilterPresets,
    setSavedFilterPresets,
    showFilterPresets,
    setShowFilterPresets,
    
    // Handlers
    handleApplyFilterPreset,
    refreshFilterPresets,
    clearAllFilters
  };
};
